import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import useSWR from 'swr'

import { loadLotoPageDetails, type LotoPageDetails } from './lotoPageLookups'
import type { LotoAttachment, LotoReportScope } from './lotoReportSchemas'

type PageDetailsKey = [string, 'loto-report-pages', string, string, string]

export type UseLotoPageDetailsResult = {
  details: LotoPageDetails | undefined
  isLoading: boolean
  error: Error | undefined
}

function attachedPagesSignature(attachments: LotoAttachment[]): string {
  return [
    ...new Set(
      attachments.flatMap(({ system_document_id, page_ids }) =>
        page_ids.map((pageId) => `${system_document_id}/${pageId}`)
      )
    ),
  ]
    .sort()
    .join('\n')
}

export function useLotoPageDetails(
  scope: LotoReportScope,
  attachments: LotoAttachment[]
): UseLotoPageDetailsResult {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const endpoint = `${ROLODEX_URL}/api/v2/entries`
  const signature = attachedPagesSignature(attachments)
  const key: PageDetailsKey | null =
    isAuthenticated && signature
      ? [
          endpoint,
          'loto-report-pages',
          scope.organization,
          scope.site,
          signature,
        ]
      : null
  const { data, error, isLoading } = useSWR<
    LotoPageDetails,
    Error,
    PageDetailsKey | null
  >(
    key,
    () => loadLotoPageDetails(authorizedAxios, endpoint, scope, attachments),
    { keepPreviousData: true }
  )
  return { details: data, isLoading, error }
}
